import axios from '../components/axios/axiosconf';
import { formatearFecha, formatearMoneda } from '../components/ui/reportes/liquidacionProfesionalPrint';

/** Observación sugerida al generar egreso / documento soporte desde liquidación. */
export function buildObservacionLiquidacionHonorarios(liquidacion) {
    const periodo = [
        formatearFecha(liquidacion?.fecha_inicio),
        formatearFecha(liquidacion?.fecha_fin),
    ].filter(Boolean).join(' a ');
    const valor = formatearMoneda(liquidacion?.valor_total);
    return `LIQUIDACION DE HONORARIOS ${periodo}${periodo ? ' ' : ''}VALOR TOTAL ${valor}`.trim();
}

/**
 * Busca proveedor del cliente por numero_documento del profesional.
 * Si no existe, lo crea con datos del usuario y valores fiscales quemados.
 * @returns {Promise<{proveedorId:number, nombreProveedor:string, tipoDocumento:string, numeroDocumento:string, observaciones:string, valorTotal:number|string, liquidacionId:number}>}
 */
export async function resolverProveedorDesdeLiquidacion(liquidacion) {
    if (!liquidacion?.users_id && !liquidacion?.profesional_numero_documento) {
        throw new Error('La liquidación no tiene profesional asociado.');
    }

    const userdata = JSON.parse(localStorage.getItem('userdata') || '{}');
    const uid = userdata.user_id;
    if (!uid) {
        throw new Error('Sesión no válida.');
    }

    let numeroDocumento = String(liquidacion.profesional_numero_documento || '').trim();
    let nombreProveedor = String(liquidacion.profesional_nombre || '').trim() || 'Profesional';
    let email = liquidacion.profesional_email || '';
    let celular = liquidacion.profesional_celular || '';
    let telefono = liquidacion.profesional_telefono || '';
    let direccion = liquidacion.profesional_direccion || '';
    let ciudad = liquidacion.profesional_ciudad != null && liquidacion.profesional_ciudad !== ''
        ? String(liquidacion.profesional_ciudad)
        : '';

    // Completar ciudad/datos faltantes desde detalle o usuario
    if ((!ciudad || !numeroDocumento) && liquidacion.id) {
        try {
            const det = await axios.get(`/liquidacion-profesional/${liquidacion.id}`);
            const d = det.data || {};
            if (!numeroDocumento) {
                numeroDocumento = String(d.profesional_numero_documento || '').trim();
            }
            nombreProveedor = String(d.profesional_nombre || nombreProveedor).trim();
            email = email || d.profesional_email || '';
            celular = celular || d.profesional_celular || '';
            telefono = telefono || d.profesional_telefono || '';
            direccion = direccion || d.profesional_direccion || '';
            if (!ciudad && d.profesional_ciudad != null && d.profesional_ciudad !== '') {
                ciudad = String(d.profesional_ciudad);
            }
            if (!liquidacion.fecha_inicio) {
                liquidacion = { ...liquidacion, ...d };
            }
        } catch (e) {
            console.warn('No se pudo cargar detalle de liquidación:', e);
        }
    }

    if ((!ciudad || !numeroDocumento) && liquidacion.users_id) {
        try {
            const uRes = await axios.get('/user', { params: { id: liquidacion.users_id, uid } });
            const u = Array.isArray(uRes.data) ? uRes.data[0] : uRes.data;
            if (u) {
                if (!numeroDocumento) {
                    numeroDocumento = String(u.numero_documento || '').trim();
                }
                if (!nombreProveedor || nombreProveedor === 'Profesional') {
                    nombreProveedor = `${u.name || ''} ${u.lastname || ''}`.trim() || nombreProveedor;
                }
                email = email || u.email || '';
                celular = celular || u.celular || '';
                telefono = telefono || u.telefono || '';
                direccion = direccion || u.direccion || '';
                if (!ciudad) {
                    const ciudadUser = u.id_ciudad != null && u.id_ciudad !== '' ? u.id_ciudad : u.ciudad;
                    if (ciudadUser != null && ciudadUser !== '') {
                        ciudad = String(ciudadUser);
                    }
                }
            }
        } catch (e) {
            console.warn('No se pudo cargar usuario del profesional:', e);
        }
    }

    if (!numeroDocumento) {
        throw new Error('El profesional no tiene número de documento. Complételo en usuarios e intente de nuevo.');
    }

    const docNorm = numeroDocumento.replace(/\s+/g, '');

    const buscarEnLista = (lista) =>
        (Array.isArray(lista) ? lista : []).find(
            (p) => String(p.numero_documento || '').replace(/\s+/g, '') === docNorm
        );

    let proveedoresRes = await axios.get('/proveedores', { params: { uid } });
    let encontrado = buscarEnLista(proveedoresRes.data);

    if (!encontrado) {
        await axios.post('/proveedor', {
            uid,
            nombre_proveedor: nombreProveedor,
            tipo_documento: 'NIT',
            numero_documento: numeroDocumento,
            direccion: direccion || '',
            telefono: telefono || '',
            celular: celular || '',
            email: email || '',
            contacto: nombreProveedor,
            ciudad: ciudad || '',
            codigo_responsabilidad: 'No responsable',
            codigo_impuesto: 'No causa',
            codigo_regimen: 'No responsable de IVA',
        });

        proveedoresRes = await axios.get('/proveedores', { params: { uid } });
        encontrado = buscarEnLista(proveedoresRes.data);
    }

    if (!encontrado) {
        throw new Error('No se pudo crear o localizar el proveedor del profesional.');
    }

    return {
        proveedorId: encontrado.id,
        nombreProveedor: encontrado.nombre_proveedor,
        tipoDocumento: encontrado.tipo_documento || 'NIT',
        numeroDocumento: encontrado.numero_documento,
        observaciones: buildObservacionLiquidacionHonorarios(liquidacion),
        valorTotal: liquidacion.valor_total,
        liquidacionId: liquidacion.id,
    };
}
